import {
  bookingsLoginUserAccountSeeAllServicesClick,
  bookingsUoUMemberAreaCancellationFeeModalSrc16Evid588,
} from '@wix/bi-logger-bookings-data/v2';
import {
  Trans,
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import React, { useEffect, useState } from 'react';
import Link from '../../../../../components/MyBookings/Components/Link';
import { Modal } from '../../../../../components/MyBookings/Components/Modal';
import { Reschedule } from '../../../../../components/MyBookings/Widget/ActionsButtons/Reschedule';
import { CancellationModalDataHooks } from '../../../../../components/MyBookings/Widget/datahooks';
import {
  ActionButtonUiTypes,
  BookingsGroup,
  CloseModalTriggers,
  RescheduleButtonOrigin,
} from '../../../../../types';
import {
  CancelClickOrigin,
  CancellationFeeModalActions,
  CancellationFeeModalComponents,
  CancellationFeeModalElements,
  CancelModalClickValues,
  MemberAreaClickActions,
} from '../../../../../types/Bi';
import { FeeInfo } from '../../../../../types/CancellationFeeInfo';
import { isRescheduleAble } from '../../../../../utils/bookInfo';
import { useBiLogger } from '../../../../bi/biLoggerFactory';
import { useApplicationContext } from '../../../../hooks/useApplicationContext';
import { classes } from '../CancellationModal.st.css';
import { BookingPolicyModalContent } from './PolicyModal';

export interface CancellationFeeModalContentProps {
  group: BookingsGroup;
  feeInfo: FeeInfo;
}

export const CancellationFeeModalContent: React.FC<
  CancellationFeeModalContentProps
> = ({ group, feeInfo }) => {
  const state = useApplicationContext();
  const [isBookingPolicyModalOpen, setIsBookingPolicyModalOpen] =
    useState<boolean>(false);
  const { actions } = useApplicationContext();
  const { t } = useTranslation();
  const biLogger = useBiLogger(state);
  const { isMobile } = useEnvironment();
  const { experiments } = useExperiments();
  const totalFeeToDisplay = feeInfo.totalFee;
  const policies = feeInfo.policies;

  useEffect(() => {
    biLogger?.report(
      bookingsUoUMemberAreaCancellationFeeModalSrc16Evid588({
        element: CancellationFeeModalElements.CANCELLATION_FEE_UOU,
        component: CancellationFeeModalComponents.MODAL,
        action: CancellationFeeModalActions.load,
      }),
    );
  }, []);

  const handleCancelButtonClick = () => {
    actions.clickOnCancel({
      group,
      origin: CancelClickOrigin.CancellationFeeModal,
    });
    actions.closeModal({});
  };

  const handleCloseButtonClick = (isSecondaryButtonClicked?: boolean) => {
    actions.closeModal({
      triggeredBy: CloseModalTriggers.CloseModal,
      origin: CancelClickOrigin.CancellationFeeModal,
      ...(isSecondaryButtonClicked
        ? { clickValue: CancelModalClickValues.KeepMyBooking }
        : {}),
    });
    actions.deleteCancellationFeeInfo({ group });
  };

  const onBookingPolicyOpen = () => {
    biLogger?.report(
      bookingsLoginUserAccountSeeAllServicesClick({
        action: MemberAreaClickActions.ViewPolicy,
      }),
    );
    setIsBookingPolicyModalOpen(true);
  };

  const onBookingPolicyClose = () => {
    setIsBookingPolicyModalOpen(false);
  };

  const renderCancellationFeeModalContent = () => (
    <Modal
      dataHook={CancellationModalDataHooks.CancellationFee}
      isLoading={!totalFeeToDisplay}
      modalTitle={t('app.my-bookings-widget.cancellation-fee-modal.title')}
      modalSubtitle={
        <Trans
          i18nKey="app.my-bookings-widget.cancellation-fee-modal.content"
          values={{
            amount: totalFeeToDisplay?.formattedValue,
          }}
        >
          <Link
            data-hook={
              CancellationModalDataHooks.ShowBookingPolicyLinkFromCancelFee
            }
            onClick={onBookingPolicyOpen}
          />
        </Trans>
      }
      primaryButton={{
        context: {
          label: t(
            'app.my-bookings-widget.cancellation-fee-modal.primary-button.label',
          ),
          onClick: handleCancelButtonClick,
        },
      }}
      secondaryButton={{
        context: {
          label: t(
            'app.my-bookings-widget.cancellation-fee-modal.secondary-button.label',
          ),
          onClick: () => handleCloseButtonClick(true),
        },
      }}
    />
  );

  const renderCancellationFeeModalWithRescheduleContent = () => (
    <Modal
      onClose={handleCloseButtonClick}
      dataHook={CancellationModalDataHooks.CancellationFeeWithReschedule}
      isLoading={!totalFeeToDisplay}
      modalTitle={t(
        'app.my-bookings-widget.cancellation-fee-reschedule-modal.title',
      )}
      modalSubtitle={
        <Trans
          i18nKey="app.my-bookings-widget.cancellation-fee-reschedule-modal.content"
          values={{
            amount: totalFeeToDisplay?.formattedValue,
          }}
        >
          <Link
            data-hook={
              CancellationModalDataHooks.ShowBookingPolicyLinkFromRescheduleCancelFee
            }
            onClick={onBookingPolicyOpen}
          />
        </Trans>
      }
      primaryButton={{
        context: {
          label: t(
            'app.my-bookings-widget.cancellation-fee-reschedule-modal.primary-button.label',
          ),
          onClick: handleCancelButtonClick,
        },
      }}
      secondaryButton={{
        component: (
          <Reschedule
            origin={RescheduleButtonOrigin.CancellationFeeModal}
            label={t(
              'app.my-bookings-widget.cancellation-fee-reschedule-modal.secondary-button.label',
            )}
            group={group}
            fullWidthButton={isMobile}
            type={ActionButtonUiTypes.BUTTON}
            className={classes.rescheduleButton}
          />
        ),
      }}
    />
  );

  const renderModalContent = () => {
    if (isBookingPolicyModalOpen && policies) {
      return (
        <BookingPolicyModalContent
          policies={policies}
          onClose={onBookingPolicyClose}
        />
      );
    } else if (isRescheduleAble(group, experiments)) {
      return renderCancellationFeeModalWithRescheduleContent();
    } else {
      return renderCancellationFeeModalContent();
    }
  };

  return renderModalContent();
};
